/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { Carousel } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';

import { INosYoutubePlaylistData } from './nos-youtube-playlist.data';
import { IHeadingData, INosYoutubePlaylistProps } from './nos-youtube-playlist.props.autogenerated';

export interface IYoutubeThumbnail {
    url: string;
}

export interface IYoutubeVideo {
    id: string;
    title: string;
    thumbnail: IYoutubeThumbnail;
}

interface IPlaylistItemSnippet {
    title?: string;
    resourceId?: {
        videoId?: string;
    };
    thumbnails?: {
        maxres?: IYoutubeThumbnail;
        standard?: IYoutubeThumbnail;
        high?: IYoutubeThumbnail;
        medium?: IYoutubeThumbnail;
        default?: IYoutubeThumbnail;
    };
}

interface IPlaylistItemsResponse {
    items?: {
        snippet?: IPlaylistItemSnippet;
    }[];
}

export interface INosYoutubePlaylistState {
    videos: IYoutubeVideo[];
    isLoading: boolean;
    errorText?: string;
    isModalOpen: boolean;
    activeVideo?: IYoutubeVideo;
    activeCarouselIndex: number;
    itemsPerView: number;
}

export interface INosYoutubePlaylistViewProps extends INosYoutubePlaylistProps<INosYoutubePlaylistData> {
    className: string;
    heading?: JSX.Element;
    thumbnails: JSX.Element;
    playerModal?: JSX.Element;
}

type IPlaylistConfig = INosYoutubePlaylistProps<INosYoutubePlaylistData>['config'] & {
    heading?: IHeadingData;
    useCarousel?: boolean;
};

type IPlaylistResources = INosYoutubePlaylistProps<INosYoutubePlaylistData>['resources'] & {
    loadingText: string;
    invalidConfigText: string;
    loadingErrorText: string;
    noVideosText: string;
    previousSlideAriaLabel: string;
    nextSlideAriaLabel: string;
    paginationIndicatorAriaLabel: string;
};

class NosYoutubePlaylist extends React.PureComponent<INosYoutubePlaylistProps<INosYoutubePlaylistData>, INosYoutubePlaylistState> {
    public state: INosYoutubePlaylistState = {
        videos: [],
        isLoading: true,
        errorText: undefined,
        isModalOpen: false,
        activeVideo: undefined,
        activeCarouselIndex: 0,
        itemsPerView: 1
    };

    public componentDidMount(): void {
        this._updateItemsPerView();
        window.addEventListener('resize', this._updateItemsPerView);
        void this._loadVideos();
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this._updateItemsPerView);
    }

    public componentDidUpdate(prevProps: INosYoutubePlaylistProps<INosYoutubePlaylistData>): void {
        const { apiKey, playlistId, maxResults } = this.props.config;
        if (
            apiKey !== prevProps.config.apiKey ||
            playlistId !== prevProps.config.playlistId ||
            maxResults !== prevProps.config.maxResults
        ) {
            void this._loadVideos();
        }
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => {
        const config = this.props.config as IPlaylistConfig;

        if (config.heading) {
            config.heading.text = event.target.value;
        }
    };

    public render(): JSX.Element {
        const config = this.props.config as IPlaylistConfig;
        const heading = config.heading?.text ? (
            <Msdyn365.Text
                className='nos-youtube-playlist__heading'
                tag={config.heading.tag || 'h3'}
                text={config.heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        ) : (
            undefined
        );

        const viewProps: INosYoutubePlaylistViewProps = {
            ...this.props,
            className: classnames('nos-youtube-playlist', config.className),
            heading,
            thumbnails: this._renderContent(),
            playerModal: this.state.isModalOpen ? this._renderPlayerModal() : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _loadVideos = async (): Promise<void> => {
        const { apiKey, playlistId, maxResults } = this.props.config;
        const resources = this.props.resources as IPlaylistResources;

        if (!apiKey?.trim() || !playlistId?.trim()) {
            this.setState({
                videos: [],
                isLoading: false,
                errorText: resources.invalidConfigText,
                isModalOpen: false,
                activeVideo: undefined
            });
            return;
        }

        try {
            const safeMaxResults = this._getSafeMaxResults(maxResults);
            const params = new URLSearchParams({
                part: 'snippet',
                playlistId: playlistId.trim(),
                maxResults: safeMaxResults.toString(),
                key: apiKey.trim()
            });

            const response = await fetch(`https://www.googleapis.com/youtube/v3/playlistItems?${params.toString()}`);
            if (!response.ok) {
                throw new Error(`YouTube API request failed with status ${response.status}`);
            }

            const payload = (await response.json()) as IPlaylistItemsResponse;
            const videos = this._mapVideos(payload);

            this.setState({
                videos,
                isLoading: false,
                errorText: undefined,
                activeCarouselIndex: 0
            });
        } catch (_error) {
            this.setState({
                videos: [],
                isLoading: false,
                errorText: resources.loadingErrorText
            });
        }
    };

    private readonly _mapVideos = (payload: IPlaylistItemsResponse): IYoutubeVideo[] => {
        return (
            payload.items
                ?.map(item => {
                    const snippet = item.snippet;
                    const videoId = snippet?.resourceId?.videoId;
                    const thumbnail =
                        snippet?.thumbnails?.high ||
                        snippet?.thumbnails?.medium ||
                        snippet?.thumbnails?.default ||
                        snippet?.thumbnails?.standard ||
                        snippet?.thumbnails?.maxres;

                    if (!videoId || !thumbnail?.url) {
                        return undefined;
                    }

                    return {
                        id: videoId,
                        title: snippet?.title || videoId,
                        thumbnail
                    };
                })
                .filter((video): video is IYoutubeVideo => Boolean(video)) || []
        );
    };

    private readonly _getSafeMaxResults = (maxResults?: number): number => {
        if (!maxResults) {
            return 12;
        }

        return Math.min(50, Math.max(1, Math.floor(maxResults)));
    };

    private readonly _renderContent = (): JSX.Element => {
        const resources = this.props.resources as IPlaylistResources;
        const config = this.props.config as IPlaylistConfig;
        const { isLoading, errorText, videos, activeCarouselIndex } = this.state;

        if (isLoading) {
            return <div className='nos-youtube-playlist__state'>{resources.loadingText}</div>;
        }

        if (errorText) {
            return <div className='nos-youtube-playlist__state nos-youtube-playlist__state--error'>{errorText}</div>;
        }

        if (videos.length === 0) {
            return <div className='nos-youtube-playlist__state'>{resources.noVideosText}</div>;
        }

        if (config.useCarousel) {
            const carouselItems = this._buildCarouselItems(videos);
            const pageCount = carouselItems.length;

            return (
                <Carousel
                    className='nos-youtube-playlist__carousel'
                    activeIndex={activeCarouselIndex}
                    items={carouselItems}
                    next={this._nextSlide}
                    previous={this._previousSlide}
                    onIndicatorsClickHandler={this._setSlide}
                    handleOnExited={this._handleCarouselTransition}
                    handleOnExiting={this._handleCarouselTransition}
                    directionTextPrev={resources.previousSlideAriaLabel}
                    directionTextNext={resources.nextSlideAriaLabel}
                    indicatorAriaText={resources.paginationIndicatorAriaLabel}
                    showPaginationTooltip={true}
                    hideIndicator={pageCount <= 1}
                    hideFlipper={pageCount <= 1}
                    keyboard={true}
                />
            );
        }

        const items = videos.map(video => this._renderThumbnailButton(video));
        return <div className='nos-youtube-playlist__grid'>{items}</div>;
    };

    private readonly _buildCarouselItems = (videos: IYoutubeVideo[]): JSX.Element[] => {
        return this._chunkVideos(videos, this.state.itemsPerView).map((group, index) => (
            <div className='nos-youtube-playlist__grid' key={`slide-${index}`}>
                {group.map(video => this._renderThumbnailButton(video))}
            </div>
        ));
    };

    private readonly _chunkVideos = (videos: IYoutubeVideo[], chunkSize: number): IYoutubeVideo[][] => {
        const safeChunkSize = Math.max(1, chunkSize);
        const chunked: IYoutubeVideo[][] = [];

        for (let index = 0; index < videos.length; index += safeChunkSize) {
            chunked.push(videos.slice(index, index + safeChunkSize));
        }

        return chunked;
    };

    private readonly _getCarouselPageCount = (): number => {
        const { videos, itemsPerView } = this.state;
        return Math.ceil(videos.length / Math.max(1, itemsPerView));
    };

    private readonly _getItemsPerView = (): number => {
        const gridSettings = this.props.context.request?.gridSettings;

        if (!gridSettings?.xs?.w || !gridSettings?.sm?.w) {
            return 1;
        }

        // gridSettings widths declared desktop first
        // Convert to min-width thresholds for media-query styles
        const tabletMinWidth = gridSettings.xs.w + 1;
        const desktopMinWidth = gridSettings.sm.w + 1;

        if (window.matchMedia(`(min-width: ${desktopMinWidth}px)`).matches) {
            return 4;
        }

        if (window.matchMedia(`(min-width: ${tabletMinWidth}px)`).matches) {
            return 2;
        }

        return 1;
    };

    private readonly _updateItemsPerView = (): void => {
        const nextItemsPerView = this._getItemsPerView();

        if (nextItemsPerView !== this.state.itemsPerView) {
            this.setState({
                itemsPerView: nextItemsPerView,
                activeCarouselIndex: 0
            });
        }
    };

    private readonly _renderThumbnailButton = (video: IYoutubeVideo): JSX.Element => {
        return (
            <button
                key={video.id}
                className='nos-youtube-playlist__thumbnail-button'
                type='button'
                onClick={() => this._openVideo(video)}
                aria-label={video.title}
            >
                <img className='nos-youtube-playlist__thumbnail-image' src={video.thumbnail.url} alt={video.title} />
                <span className='nos-youtube-playlist__thumbnail-title'>{video.title}</span>
            </button>
        );
    };

    private readonly _renderPlayerModal = (): JSX.Element | undefined => {
        const { activeVideo, isModalOpen } = this.state;

        if (!activeVideo) {
            return undefined;
        }

        const videoId = encodeURIComponent(activeVideo.id);
        const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

        return (
            <Modal isOpen={isModalOpen} className='nos-youtube-playlist__modal' toggle={this._closeModal}>
                <ModalHeader toggle={this._closeModal}>{activeVideo.title}</ModalHeader>
                <ModalBody>
                    <div className='nos-youtube-playlist__video-wrapper'>
                        <iframe
                            className='nos-youtube-playlist__video-frame'
                            src={embedUrl}
                            title={activeVideo.title}
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            referrerPolicy='strict-origin-when-cross-origin'
                            allowFullScreen={true}
                        />
                    </div>
                </ModalBody>
            </Modal>
        );
    };

    private readonly _openVideo = (video: IYoutubeVideo): void => {
        this.setState({
            activeVideo: video,
            isModalOpen: true
        });
    };

    private readonly _closeModal = (): void => {
        this.setState({
            isModalOpen: false,
            activeVideo: undefined
        });
    };

    private readonly _setSlide = (index: number): void => {
        const pageCount = this._getCarouselPageCount();
        const safeIndex = Math.max(0, Math.min(index, Math.max(pageCount - 1, 0)));
        this.setState({ activeCarouselIndex: safeIndex });
    };

    private readonly _nextSlide = (): void => {
        this.setState(previousState => {
            const pageCount = this._getCarouselPageCount();
            const next = previousState.activeCarouselIndex + 1;
            const maxIndex = pageCount - 1;
            return { activeCarouselIndex: next > maxIndex ? 0 : next };
        });
    };

    private readonly _previousSlide = (): void => {
        this.setState(previousState => {
            const pageCount = this._getCarouselPageCount();
            const previous = previousState.activeCarouselIndex - 1;
            const maxIndex = pageCount - 1;
            return { activeCarouselIndex: previous < 0 ? maxIndex : previous };
        });
    };

    private readonly _handleCarouselTransition = (): void => {
        return;
    };
}

export default NosYoutubePlaylist;
